import { useAppSelector } from '@dealstack/frontend/app/hooks/redux';
import {
	buildDocumentFilters,
	CounterpartyOptionGroupKey,
	EligibleDocumentFilterMode,
	getSignatureStatusOptions,
	readDocumentsNavigationState,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.utils';
import { createBulkActions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/InvestmentProgrammeDocuments.utils';
import { BulkAssignCounterpartyModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkAssignCounterpartyModal';
import { BulkAddCounterpartyModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkAddCounterparty/BulkAddCounterpartyModal';
import { BulkInviteAuthorisedSignatoryModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkInviteAuthorisedSignatoryModal';
import type { BulkInviteAuthorisedSignatoryConfirmPayload } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkInviteAuthorisedSignatoryModal.types';
import { createInvestmentProgrammeDocumentsColumns } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/createInvestmentProgrammeDocumentsColumns';
import { BulkRemindSignersModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkRemindSignersModal';
import {
	BULK_ALLOWED_METHODS,
	useBulkEidEasySign,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useBulkEidEasySign';
import { useInvestmentProgrammeDocuments } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useInvestmentProgrammeDocuments';
import { useInvestmentProgrammeSigningRoles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useInvestmentProgrammeSigningRoles';
import { useBulkAssignCounterpartiesPreview } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkAssignCounterpartiesPreview';
import { useBulkAssignCounterparty } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkAssignCounterparty';
import { useBulkAssignCounterpartyStatus } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkAssignCounterpartyStatus';
import { useBulkAddCounterpartyPreview } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkAddCounterpartyPreview';
import { useBulkAddCounterparty } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkAddCounterparty';
import { useBulkAddCounterpartyStatus } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkAddCounterpartyStatus';
import type { BulkAddCounterpartyDetails } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkAddCounterparty/BulkAddCounterpartyModal.types';
import { toAssignableUsers } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/AddCounterpartyModal.utils';
import { useBulkClaimGenerationStatus } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkClaimGenerationStatus';
import { useBulkClaimOnBehalf } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkClaimOnBehalf';
import { useBulkClaimOnBehalfPreview } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkClaimOnBehalfPreview';
import { useBulkDownloadDocumentsExcel } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkDownloadDocumentsExcel';
import { useBulkFileDownload } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkFileDownload';
import { useBulkInviteAuthorisedSignatoriesPreview } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkInviteAuthorisedSignatoriesPreview';
import { useBulkInviteAuthorisedSignatory } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkInviteAuthorisedSignatory';
import { useBulkReissueDocuments } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkReissueDocuments';
import { useBulkRemindSigners } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkRemindSigners';
import { useBulkRemindSignersStatus } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkRemindSignersStatus';
import { useBulkRevokeDocuments } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkRevokeDocuments';
import { useProgrammeUsers } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useProgrammeUsers';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils';
import { selectUserId } from '@dealstack/frontend/app/redux/user.selectors';
import { DocumentTable } from '@dealstack/frontend/shared/components/Documents/DocumentTable/DocumentTable';
import { EidEasySigningModal } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal';
import { RevokeDocumentModal } from '@dealstack/frontend/shared/components/RevokeDocumentModal/RevokeDocumentModal';
import { getTableSelectionState } from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { getTableMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { UseMantineTableDataOperationsPartialProps } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState';
import { useMantineTableWithPaginationFilteringSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { BulkClaimSelectionBody } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import {
	useTranslationDocuments,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { BulkAssignAction } from '@dealstack/shared/lib/bulkAssignAction';
import {
	BulkAddCounterpartyDto,
	BulkAssignCounterpartyDto,
	BulkRevokeDocumentsDto,
	ESignProvider,
	InvestmentProgrammeDocumentRes,
} from '@dealstack/shared/lib/generated';
import type { BulkRemindSignersDto } from '@dealstack/shared/lib/backendClient';
import { Flex } from '@mantine/core';
import { Row, Table } from '@tanstack/react-table';
import { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { QuickFilterOption } from '@dealstack/frontend/shared/components/Table/MantineTable/QuickFiltersSelect/QuickFiltersSelect.types';
import { InvestorSigningStatus } from '@dealstack/shared/lib/common/business/documentWorkflowStatus';
import {
	InvestmentProgrammeDocumentsDataOperations,
	InvestmentProgrammeDocumentsFilter,
} from './Documents.utils.types';
import { useGetDealClassifiers } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/hooks/useGetDealClassifiers';
import { flattenDealClassifiersToOptions } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/utils/flattenDealClassifiersToOptions';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { BulkClaimOnBehalfModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkClaimOnBehalf/BulkClaimOnBehalfModal';
import { BulkApplyDateModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkApplyDateModal';
import type { BulkApplyDateSelection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkApplyDateModal.types';

type CounterpartyOption = {
	value: string;
	label: string;
	groupKey: CounterpartyOptionGroupKey;
	groupLabel: string;
};

export const InvestmentProgrammeDocuments = () => {
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const tDocuments = useTranslationDocuments();
	const tMipManage = useTranslationMipManage();
	const currentUserId = useAppSelector(selectUserId);
	const navigate = useNavigate();
	const { value: automatedRemindersEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.AUTOMATED_EMAIL_REMINDERS
	);

	// Seeded, not synced: another page can send the manager here already in a bulk-action mode
	// (the add-counterparty panel does, when a party can only be added by revoking and re-sending).
	// A lazy initial value applies it on mount without an effect, and it is theirs to change after.
	const { state: navigationState } = useLocation();
	const [eligibleDocumentFilterMode, setEligibleDocumentFilterMode] =
		useState<EligibleDocumentFilterMode | null>(
			() =>
				readDocumentsNavigationState(navigationState).enterEligibleDocumentFilterMode ??
				null
		);

	const initialTableOptions: UseMantineTableDataOperationsPartialProps = {
		initialPagination: { pageIndex: 0, pageSize: 50 },
		initialSorting: [{ id: 'createdAt', desc: true }],
		initialShowFilters: true,
		queryScope: [eligibleDocumentFilterMode],
	};

	const { data: counterpartyRoles } = useInvestmentProgrammeSigningRoles({
		dealId,
		programmeId: mipId,
	});

	const counterpartyOptions = useMemo<CounterpartyOption[]>(() => {
		const statusOptions = getSignatureStatusOptions(tMipManage);
		const roleOptions = (counterpartyRoles ?? []).map((role) => ({
			value: role.value,
			label: role.label,
			groupKey: CounterpartyOptionGroupKey.DOCUMENT_ROLE,
			groupLabel: role.group,
		}));
		return [...statusOptions, ...roleOptions];
	}, [counterpartyRoles, tMipManage]);

	const { classifiers: dealClassifiersData } = useGetDealClassifiers({
		dealId,
		filters: { filter: { getAll: true } },
	});

	const classifierOptions = useMemo(
		() => flattenDealClassifiersToOptions(dealClassifiersData?.payload?.dealClassifiers ?? []),
		[dealClassifiersData]
	);

	const initialColumns = createInvestmentProgrammeDocumentsColumns(
		counterpartyOptions,
		tMipManage,
		undefined,
		classifierOptions
	);

	const quickFilterOptions = useMemo<QuickFilterOption[]>(
		() => [
			{
				value: 'for-me',
				label: tMipManage('documents_page.quick_filters.for_me'),
				filterKey: 'userIdRequiredOnSignature',
				filterValue: currentUserId ?? '',
			},
			{
				value: 'all-documents',
				label: tMipManage('documents_page.quick_filters.all_documents'),
			},
			{
				value: 'awaiting-investor-signatures',
				label: tMipManage('documents_page.quick_filters.awaiting_investor_signatures'),
				filterKey: 'anyInvestorSignatureRequired',
				filterValue: true,
			},
			{
				value: 'awaiting-counterparty-signatures',
				label: tMipManage('documents_page.quick_filters.awaiting_counterparty_signatures'),
				filterKey: 'anyCounterpartySignatureRequired',
				filterValue: true,
			},
			{
				value: 'unassigned-counterparty-signatures',
				label: tMipManage(
					'documents_page.quick_filters.unassigned_counterparty_signatures'
				),
				filterKey: 'unassignedCounterparty',
				filterValue: true,
			},
			{
				value: 'signing-not-signed',
				label: tMipManage('documents_page.signing_status.not_signed'),
				filterKey: 'investorSigningStatus',
				filterValue: InvestorSigningStatus.NOT_SIGNED,
			},
			{
				value: 'signing-signed-self-only',
				label: tMipManage('documents_page.signing_status.signed_for_self_only'),
				filterKey: 'investorSigningStatus',
				filterValue: InvestorSigningStatus.SIGNED_SELF_ONLY,
			},
			{
				value: 'signing-signed-piv-only',
				label: tMipManage('documents_page.signing_status.signed_for_piv_only'),
				filterKey: 'investorSigningStatus',
				filterValue: InvestorSigningStatus.SIGNED_PIV_ONLY,
			},
			{
				value: 'signing-pending-witness',
				label: tMipManage('documents_page.signing_status.pending_witness'),
				filterKey: 'investorSigningStatus',
				filterValue: InvestorSigningStatus.PENDING_WITNESS,
			},
		],
		[currentUserId, tMipManage]
	);

	const { mantineTableDataOperations, dataOperations } =
		useMantineTableWithPaginationFilteringSorting<
			InvestmentProgrammeDocumentRes,
			InvestmentProgrammeDocumentsFilter
		>(initialColumns, initialTableOptions, true);

	const typedDataOperations = dataOperations as InvestmentProgrammeDocumentsDataOperations;

	const tableRef = useRef<Table<InvestmentProgrammeDocumentRes> | null>(null);

	const {
		data,
		isFetching,
		refetch: refetchDocuments,
	} = useInvestmentProgrammeDocuments({
		dealId,
		programmeId: mipId,
		dataOperations: typedDataOperations,
		eligibleDocumentFilterMode,
		currentUserId: currentUserId ?? undefined,
	});

	const bulkEidEasySign = useBulkEidEasySign({
		dealId,
		programmeId: mipId,
		onComplete: () => {
			setEligibleDocumentFilterMode(null);
			if (tableRef.current) {
				getTableMeta(tableRef.current).resetActionState?.();
			}
		},
	});

	const documentsData = data?.payload?.documents;
	const hasEidEasyDocuments = documentsData?.some(
		(doc) => doc.eSignProvider === ESignProvider.EID_EASY
	);
	const footerValues = data?.payload?.aggregateInfo;

	const columnsWithFooterValues = createInvestmentProgrammeDocumentsColumns(
		counterpartyOptions,
		tMipManage,
		footerValues,
		classifierOptions
	);

	const { mutate: bulkDownloadFiles, isPending: isBulkDownloadLoading } = useBulkFileDownload({
		tDocuments,
	});

	const bulkDownloadMipDocumentsExcelMutation = useBulkDownloadDocumentsExcel();

	const [showRevokeSimpleModal, setShowRevokeSimpleModal] = useState(false);
	const [revokeSimpleRequestBody, setRevokeSimpleRequestBody] = useState<{
		allDocumentsSelected: boolean;
		selectedDocumentWorkflowInstanceIds?: string[];
		excludedDocumentWorkflowInstanceIds?: string[];
		filters: BulkRevokeDocumentsDto['filters'];
	} | null>(null);

	const { mutate: bulkRevokeDocumentsMutate, isPending: isBulkRevokeLoading } =
		useBulkRevokeDocuments({
			dealId,
			programmeId: mipId,
			onSuccess: () => setShowRevokeSimpleModal(false),
			onSettled: () => {
				if (tableRef.current) {
					getTableMeta(tableRef.current).resetActionState?.();
				}
				setRevokeSimpleRequestBody(null);
			},
		});

	const { mutate: bulkReissueDocuments, isPending: isBulkReissueLoading } =
		useBulkReissueDocuments({
			dealId,
			programmeId: mipId,
			onSettled: () => setEligibleDocumentFilterMode(null),
		});

	const [showRemindSignersModal, setShowRemindSignersModal] = useState(false);
	const [remindSignersJobId, setRemindSignersJobId] = useState<string | null>(null);

	const { mutate: bulkRemindSignersMutate, isPending: isBulkRemindSignersLoading } =
		useBulkRemindSigners({
			dealId,
			programmeId: mipId,
			onSuccess: (data) => {
				setRemindSignersJobId(data.jobId);
			},
			onSettled: () => {
				if (tableRef.current) {
					getTableMeta(tableRef.current).resetActionState?.();
				}
			},
		});

	const { data: remindSignersJobStatus } = useBulkRemindSignersStatus({
		dealId,
		programmeId: mipId,
		jobId: remindSignersJobId,
	});

	const [showAssignCounterpartyModal, setShowAssignCounterpartyModal] = useState(false);
	const [assignCounterpartyRequestBody, setAssignCounterpartyRequestBody] = useState<{
		allDocumentsSelected: boolean;
		selectedDocumentWorkflowInstanceIds?: string[];
		excludedDocumentWorkflowInstanceIds?: string[];
		filters: BulkAssignCounterpartyDto['filters'];
	} | null>(null);
	const [assignCounterpartyJobId, setAssignCounterpartyJobId] = useState<string | null>(null);

	const [showAddCounterpartyModal, setShowAddCounterpartyModal] = useState(false);
	const [addCounterpartySelection, setAddCounterpartySelection] = useState<{
		allDocumentsSelected: boolean;
		selectedDocumentWorkflowInstanceIds?: string[];
		excludedDocumentWorkflowInstanceIds?: string[];
		filters: BulkAddCounterpartyDto['filters'];
	} | null>(null);
	const [addCounterpartyJobId, setAddCounterpartyJobId] = useState<string | null>(null);

	const [showInviteSignatoryModal, setShowInviteSignatoryModal] = useState(false);
	const [inviteSignatoryRequestBody, setInviteSignatoryRequestBody] = useState<{
		allDocumentsSelected: boolean;
		selectedDocumentWorkflowInstanceIds?: string[];
		excludedDocumentWorkflowInstanceIds?: string[];
		filters: ReturnType<typeof buildDocumentFilters>;
	} | null>(null);

	const {
		mutate: fetchInviteSignatoryPreview,
		data: inviteSignatoryPreview,
		isPending: isLoadingInviteSignatoryPreview,
		reset: resetInviteSignatoryPreview,
	} = useBulkInviteAuthorisedSignatoriesPreview({ dealId, programmeId: mipId });

	const {
		mutate: bulkInviteSignatoryMutate,
		data: inviteSignatoryResult,
		isPending: isBulkInviteLoading,
		reset: resetInviteSignatory,
	} = useBulkInviteAuthorisedSignatory({
		dealId,
		programmeId: mipId,
		onSuccess: () => {
			void refetchDocuments();
		},
		onSettled: () => {
			if (tableRef.current) {
				getTableMeta(tableRef.current).resetActionState?.();
			}
		},
	});

	const { data: programmeUsers } = useProgrammeUsers({ programmeId: mipId });

	const {
		mutate: fetchPreview,
		data: previewData,
		isPending: isLoadingPreview,
	} = useBulkAssignCounterpartiesPreview({ dealId, programmeId: mipId });

	const { mutate: bulkAssignCounterpartyMutate, isPending: isBulkAssignLoading } =
		useBulkAssignCounterparty({
			dealId,
			programmeId: mipId,
			onSuccess: (data) => {
				setAssignCounterpartyJobId(data.payload?.jobId ?? null);
			},
			onSettled: () => {
				if (tableRef.current) {
					getTableMeta(tableRef.current).resetActionState?.();
				}
			},
		});

	const { data: assignCounterpartyJobStatus } = useBulkAssignCounterpartyStatus({
		dealId,
		programmeId: mipId,
		jobId: assignCounterpartyJobId,
	});

	const {
		mutate: fetchAddCounterpartyPreview,
		data: addCounterpartyPreview,
		isPending: isLoadingAddCounterpartyPreview,
		isError: isAddCounterpartyPreviewError,
	} = useBulkAddCounterpartyPreview({ dealId, programmeId: mipId });

	const { mutate: bulkAddCounterpartyMutate, isPending: isBulkAddCounterpartyLoading } =
		useBulkAddCounterparty({
			dealId,
			programmeId: mipId,
			onSuccess: (data) => {
				setAddCounterpartyJobId(data.payload?.jobId ?? null);
			},
			onSettled: () => {
				if (tableRef.current) {
					getTableMeta(tableRef.current).resetActionState?.();
				}
			},
		});

	const { data: addCounterpartyJobStatus } = useBulkAddCounterpartyStatus({
		dealId,
		programmeId: mipId,
		jobId: addCounterpartyJobId,
	});

	const [showApplyDateModal, setShowApplyDateModal] = useState(false);
	const [applyDateSelection, setApplyDateSelection] = useState<BulkApplyDateSelection>({
		allDocumentsSelected: false,
	});

	const [showClaimOnBehalfModal, setShowClaimOnBehalfModal] = useState(false);
	const [claimGenerationJobId, setClaimGenerationJobId] = useState<string | null>(null);
	const claimPreview = useBulkClaimOnBehalfPreview({ dealId, programmeId: mipId });
	const claimExecute = useBulkClaimOnBehalf({
		dealId,
		programmeId: mipId,
		onSuccess: (data) => {
			showToastV2({
				state: 'success',
				title: tMipManage('documents_page.toast.claim_on_behalf.success'),
			});
			setClaimGenerationJobId(data.payload?.generationJobId ?? null);
			void refetchDocuments();
		},
		onSettled: () => {
			if (tableRef.current) {
				getTableMeta(tableRef.current).resetActionState?.();
			}
		},
	});

	const { data: claimGenerationJobStatus } = useBulkClaimGenerationStatus({
		dealId,
		programmeId: mipId,
		jobId: claimGenerationJobId,
	});

	const handleBulkExportDocuments = () => {
		bulkDownloadMipDocumentsExcelMutation.mutate({
			programmeId: mipId,
			dealId,
			dataOperations: typedDataOperations,
		});
	};

	const handleBulkDownloadDocuments = (table: Table<InvestmentProgrammeDocumentRes>) => {
		const { greedySelecting } = getTableMeta(table);
		const selectionState = getTableSelectionState(table);

		// This is an optimization to make the lookup faster
		const docIdToFileIdMap = new Map(
			documentsData?.map((doc) => [doc.documentWorkflowInstanceId, doc.fileId]) ?? []
		);

		const selectedFileIds = Object.entries(selectionState)
			.filter(([, selected]) => selected)
			.map(([rowId]) => docIdToFileIdMap.get(rowId))
			.filter((fileId): fileId is string => !!fileId);

		bulkDownloadFiles({
			dealId,
			programmeId: mipId,
			body: {
				allFilesSelected: greedySelecting,
				selectedFileIds: greedySelecting ? undefined : selectedFileIds,
				excludedFileIds: greedySelecting ? selectedFileIds : undefined,
				filters: buildDocumentFilters(typedDataOperations),
			},
		});
	};

	const handleBulkReissueDocuments = (table: Table<InvestmentProgrammeDocumentRes>) => {
		const { greedySelecting } = getTableMeta(table);
		const selectionState = getTableSelectionState(table);

		const selectedDocumentWorkflowInstanceIds = Object.entries(selectionState)
			.filter(([, selected]) => selected)
			.map(([rowId]) => rowId)
			.filter((id): id is string => !!id);

		bulkReissueDocuments({
			allDocumentsSelected: greedySelecting,
			selectedDocumentWorkflowInstanceIds: greedySelecting
				? undefined
				: selectedDocumentWorkflowInstanceIds,
			excludedDocumentWorkflowInstanceIds: greedySelecting
				? selectedDocumentWorkflowInstanceIds
				: undefined,
			filters: buildDocumentFilters(typedDataOperations),
		});
	};

	const handleBulkRemindSigners = (table: Table<InvestmentProgrammeDocumentRes>) => {
		tableRef.current = table;
		const { greedySelecting } = getTableMeta(table);
		const selectionState = getTableSelectionState(table);

		const selectedDocumentWorkflowInstanceIds = Object.entries(selectionState)
			.filter(([, selected]) => selected)
			.map(([rowId]) => rowId)
			.filter((id): id is string => !!id);

		const body: BulkRemindSignersDto = {
			allDocumentsSelected: greedySelecting,
			selectedDocumentWorkflowInstanceIds: greedySelecting
				? undefined
				: selectedDocumentWorkflowInstanceIds,
			excludedDocumentWorkflowInstanceIds: greedySelecting
				? selectedDocumentWorkflowInstanceIds
				: undefined,
			filters: buildDocumentFilters(typedDataOperations),
		};

		setShowRemindSignersModal(true);
		bulkRemindSignersMutate(body);
	};

	const handleRemindSignersClose = () => {
		setShowRemindSignersModal(false);
		setRemindSignersJobId(null);
	};

	const handleBulkRevokeDocuments = (table: Table<InvestmentProgrammeDocumentRes>) => {
		tableRef.current = table;
		const { greedySelecting } = getTableMeta(table);
		const selectionState = getTableSelectionState(table);

		const selectedDocumentWorkflowInstanceIds = Object.entries(selectionState)
			.filter(([, selected]) => selected)
			.map(([rowId]) => rowId)
			.filter((id): id is string => !!id);

		setRevokeSimpleRequestBody({
			allDocumentsSelected: greedySelecting,
			selectedDocumentWorkflowInstanceIds: greedySelecting
				? undefined
				: selectedDocumentWorkflowInstanceIds,
			excludedDocumentWorkflowInstanceIds: greedySelecting
				? selectedDocumentWorkflowInstanceIds
				: undefined,
			filters: buildDocumentFilters(typedDataOperations),
		});
		setShowRevokeSimpleModal(true);
	};

	const handleBulkRevokeConfirm = (reason: string) => {
		if (!revokeSimpleRequestBody) {
			return;
		}
		bulkRevokeDocumentsMutate({
			...revokeSimpleRequestBody,
			reason,
		});
	};

	const handleBulkSign = (table: Table<InvestmentProgrammeDocumentRes>) => {
		tableRef.current = table;
		const { greedySelecting } = getTableMeta(table);
		const selectionState = getTableSelectionState(table);

		const selectedDocumentWorkflowInstanceIds = Object.entries(selectionState)
			.filter(([, selected]) => selected)
			.map(([rowId]) => rowId)
			.filter((id): id is string => !!id);

		bulkEidEasySign.startBulkSign({
			allDocumentsSelected: greedySelecting,
			selectedDocumentWorkflowInstanceIds: greedySelecting
				? undefined
				: selectedDocumentWorkflowInstanceIds,
			excludedDocumentWorkflowInstanceIds: greedySelecting
				? selectedDocumentWorkflowInstanceIds
				: undefined,
			filters: buildDocumentFilters(typedDataOperations),
		});
	};

	const handleBulkAssignCounterparty = (table: Table<InvestmentProgrammeDocumentRes>) => {
		tableRef.current = table;
		const { greedySelecting } = getTableMeta(table);
		const selectionState = getTableSelectionState(table);

		const selectedDocumentWorkflowInstanceIds = Object.entries(selectionState)
			.filter(([, selected]) => selected)
			.map(([rowId]) => rowId)
			.filter((id): id is string => !!id);

		const body = {
			allDocumentsSelected: greedySelecting,
			selectedDocumentWorkflowInstanceIds: greedySelecting
				? undefined
				: selectedDocumentWorkflowInstanceIds,
			excludedDocumentWorkflowInstanceIds: greedySelecting
				? selectedDocumentWorkflowInstanceIds
				: undefined,
			filters: buildDocumentFilters(typedDataOperations),
		};

		setAssignCounterpartyRequestBody(body);
		setShowAssignCounterpartyModal(true);
		fetchPreview(body);
	};

	const handleBulkAssignCounterpartyConfirm = (
		changes: Array<{
			roleName: string;
			roleIds: string[];
			action: BulkAssignAction;
			userId: string;
		}>
	) => {
		if (!assignCounterpartyRequestBody) {
			return;
		}
		bulkAssignCounterpartyMutate({
			...assignCounterpartyRequestBody,
			changes,
		});
	};

	const handleAssignCounterpartyClose = () => {
		// A plain cancel keeps the filter and selection so the "X items selected" count stays in
		// step with the modal. Only drop the filter once the action has run (the header's "X" —
		// onActionCanceled — is the explicit full exit).
		const assignWasExecuted = assignCounterpartyJobId != null;
		setShowAssignCounterpartyModal(false);
		setAssignCounterpartyRequestBody(null);
		setAssignCounterpartyJobId(null);
		if (assignWasExecuted) {
			setEligibleDocumentFilterMode(null);
		}
	};

	// Unlike assign-counterparty, this action has no eager preview fetch here: the party name and
	// slots that shape the preview (duplicate-name, invite-downgrade) only exist once the modal's
	// own details step is submitted — see handleAddCounterpartyPreview.
	const handleBulkAddCounterparty = (table: Table<InvestmentProgrammeDocumentRes>) => {
		tableRef.current = table;
		const { greedySelecting } = getTableMeta(table);
		const selectionState = getTableSelectionState(table);

		const selectedDocumentWorkflowInstanceIds = Object.entries(selectionState)
			.filter(([, selected]) => selected)
			.map(([rowId]) => rowId)
			.filter((id): id is string => !!id);

		setAddCounterpartySelection({
			allDocumentsSelected: greedySelecting,
			selectedDocumentWorkflowInstanceIds: greedySelecting
				? undefined
				: selectedDocumentWorkflowInstanceIds,
			excludedDocumentWorkflowInstanceIds: greedySelecting
				? selectedDocumentWorkflowInstanceIds
				: undefined,
			filters: buildDocumentFilters(typedDataOperations),
		});
		setShowAddCounterpartyModal(true);
	};

	const handleAddCounterpartyPreview = (details: BulkAddCounterpartyDetails) => {
		if (!addCounterpartySelection) {
			return;
		}
		fetchAddCounterpartyPreview({
			...addCounterpartySelection,
			name: details.name,
			slots: details.slots,
		});
	};

	const handleAddCounterpartyConfirm = (details: BulkAddCounterpartyDetails) => {
		if (!addCounterpartySelection) {
			return;
		}
		bulkAddCounterpartyMutate({
			...addCounterpartySelection,
			name: details.name,
			slots: details.slots,
		});
	};

	const handleAddCounterpartyClose = () => {
		const addWasExecuted = addCounterpartyJobId != null;
		setShowAddCounterpartyModal(false);
		setAddCounterpartySelection(null);
		setAddCounterpartyJobId(null);
		if (addWasExecuted) {
			setEligibleDocumentFilterMode(null);
		}
	};

	const handleBulkInviteSignatory = (table: Table<InvestmentProgrammeDocumentRes>) => {
		tableRef.current = table;
		const { greedySelecting } = getTableMeta(table);
		const selectionState = getTableSelectionState(table);

		const selectedDocumentWorkflowInstanceIds = Object.entries(selectionState)
			.filter(([, selected]) => selected)
			.map(([rowId]) => rowId)
			.filter((id): id is string => !!id);

		const body = {
			allDocumentsSelected: greedySelecting,
			selectedDocumentWorkflowInstanceIds: greedySelecting
				? undefined
				: selectedDocumentWorkflowInstanceIds,
			excludedDocumentWorkflowInstanceIds: greedySelecting
				? selectedDocumentWorkflowInstanceIds
				: undefined,
			filters: buildDocumentFilters(typedDataOperations),
		};

		setInviteSignatoryRequestBody(body);
		setShowInviteSignatoryModal(true);
		fetchInviteSignatoryPreview(body);
	};

	const handleBulkInviteSignatoryConfirm = ({
		changes,
		cancellations,
	}: BulkInviteAuthorisedSignatoryConfirmPayload) => {
		if (!inviteSignatoryRequestBody) {
			return;
		}
		bulkInviteSignatoryMutate({
			...inviteSignatoryRequestBody,
			changes,
			cancellations,
		});
	};

	const handleInviteSignatoryClose = () => {
		setShowInviteSignatoryModal(false);
		setInviteSignatoryRequestBody(null);
		resetInviteSignatoryPreview();
		resetInviteSignatory();
		setEligibleDocumentFilterMode(null);
	};

	const handleBulkClaimOnBehalf = (table: Table<InvestmentProgrammeDocumentRes>) => {
		tableRef.current = table;
		const { greedySelecting } = getTableMeta(table);
		const selectionState = getTableSelectionState(table);

		const selectedDocumentWorkflowInstanceIds = Object.entries(selectionState)
			.filter(([, selected]) => selected)
			.map(([rowId]) => rowId)
			.filter((id): id is string => !!id);

		const body: BulkClaimSelectionBody = {
			allDocumentsSelected: greedySelecting,
			selectedDocumentWorkflowInstanceIds: greedySelecting
				? undefined
				: selectedDocumentWorkflowInstanceIds,
			excludedDocumentWorkflowInstanceIds: greedySelecting
				? selectedDocumentWorkflowInstanceIds
				: undefined,
			filters: buildDocumentFilters(typedDataOperations),
		};

		setShowClaimOnBehalfModal(true);
		claimPreview.fetchPreview(body);
	};

	const handleClaimOnBehalfConfirm = (signerIds: string[]) => {
		claimExecute.mutate({ signerIds });
	};

	const handleClaimOnBehalfClose = () => {
		// A plain cancel keeps the filter and selection so the "X items selected" count stays in
		// step with the modal. Only drop the filter once the claim has run (the header's "X" —
		// onActionCanceled — is the explicit full exit).
		const claimWasExecuted = claimExecute.data != null || claimGenerationJobId != null;
		setShowClaimOnBehalfModal(false);
		claimPreview.reset();
		claimExecute.reset();
		setClaimGenerationJobId(null);
		if (claimWasExecuted) {
			setEligibleDocumentFilterMode(null);
		}
	};

	const handleBulkApplyDate = (table: Table<InvestmentProgrammeDocumentRes>) => {
		tableRef.current = table;
		const { greedySelecting } = getTableMeta(table);
		const selectionState = getTableSelectionState(table);

		const selectedDocumentWorkflowInstanceIds = Object.entries(selectionState)
			.filter(([, selected]) => selected)
			.map(([rowId]) => rowId)
			.filter((id): id is string => !!id);

		setApplyDateSelection({
			allDocumentsSelected: greedySelecting,
			selectedDocumentWorkflowInstanceIds: greedySelecting
				? undefined
				: selectedDocumentWorkflowInstanceIds,
			excludedDocumentWorkflowInstanceIds: greedySelecting
				? selectedDocumentWorkflowInstanceIds
				: undefined,
			filters: buildDocumentFilters(typedDataOperations),
		});
		setShowApplyDateModal(true);
	};

	const handleApplyDateClose = () => {
		setShowApplyDateModal(false);
		setEligibleDocumentFilterMode(null);
		if (tableRef.current) {
			getTableMeta(tableRef.current).resetActionState?.();
		}
	};

	const handleRowClick = (row: Row<InvestmentProgrammeDocumentRes>) => {
		const documentWorkflowInstanceId = row.original.documentWorkflowInstanceId;
		if (!documentWorkflowInstanceId) {
			return;
		}

		const path = mipManagePath(MIP_MANAGE_ROUTES.DOCUMENT_DETAILS, {
			dealId,
			mipId,
			documentWorkflowInstanceId,
		});

		navigate(path);
	};

	const getEligibleDocumentFilterBannerMessage = () => {
		if (eligibleDocumentFilterMode === EligibleDocumentFilterMode.REVOKE) {
			return tMipManage('documents_page.revoke_mode_banner.message');
		}
		if (eligibleDocumentFilterMode === EligibleDocumentFilterMode.SIGN) {
			return tMipManage('documents_page.sign_mode_banner.message');
		}
		if (eligibleDocumentFilterMode === EligibleDocumentFilterMode.ASSIGN_COUNTERPARTY) {
			return tMipManage('documents_page.assign_counterparty_mode_banner.message');
		}
		if (eligibleDocumentFilterMode === EligibleDocumentFilterMode.ADD_COUNTERPARTY) {
			return tMipManage('documents_page.add_counterparty_mode_banner.message');
		}
		if (eligibleDocumentFilterMode === EligibleDocumentFilterMode.CLAIM_ON_BEHALF) {
			return tMipManage('documents_page.claim_on_behalf_mode_banner.message');
		}
		if (eligibleDocumentFilterMode === EligibleDocumentFilterMode.INVITE_AUTHORISED_SIGNATORY) {
			return tMipManage('documents_page.invite_authorised_signatory_mode_banner.message');
		}
		if (eligibleDocumentFilterMode === EligibleDocumentFilterMode.REMIND) {
			return tMipManage('documents_page.remind_mode_banner.message');
		}
		if (eligibleDocumentFilterMode === EligibleDocumentFilterMode.APPLY_DATE) {
			return tMipManage('documents_page.apply_date_mode_banner.message');
		}

		return tMipManage('documents_page.reissue_mode_banner.message');
	};

	return (
		<Flex direction="column" w="100%" gap="1rem">
			{eligibleDocumentFilterMode && (
				<InfoBox
					iconVariant="warning"
					rippleColor="orange"
					alignContent="center"
					description={getEligibleDocumentFilterBannerMessage()}
					dataTestId={TEST_ELEMENT_IDS.DOCUMENTS_ELIGIBLE_FILTER_MODE_BANNER}
				/>
			)}
			<DocumentTable<InvestmentProgrammeDocumentRes>
				getRowId={(row) => row.documentWorkflowInstanceId}
				initialColumnVisibility={{ investmentWorkflowId: false, investorId: false }}
				hasNoTopToolbar={true}
				enableSearchBar={false}
				enableColumnResizing={true}
				pinnedColumnIds={['documentName']}
				columns={columnsWithFooterValues}
				data={documentsData}
				enablePagination={true}
				enableStickyHeader={true}
				renderBulkActionsMenu={true}
				quickFilterOptions={quickFilterOptions}
				actions={() =>
					createBulkActions({
						tDocuments,
						tMipManage,
						bulkDownloadDocuments: handleBulkDownloadDocuments,
						bulkExportDocuments: handleBulkExportDocuments,
						bulkReissueDocuments: handleBulkReissueDocuments,
						bulkRevokeDocuments: handleBulkRevokeDocuments,
						bulkSignDocuments: handleBulkSign,
						bulkAddCounterparty: handleBulkAddCounterparty,
						bulkAssignCounterparty: handleBulkAssignCounterparty,
						bulkClaimOnBehalf: handleBulkClaimOnBehalf,
						bulkInviteAuthorisedSignatory: handleBulkInviteSignatory,
						bulkRemindSigners: handleBulkRemindSigners,
						bulkApplyDate: handleBulkApplyDate,
						showRemindSigners: automatedRemindersEnabled,
						shouldDisableActions:
							isBulkDownloadLoading ||
							isBulkReissueLoading ||
							isBulkRevokeLoading ||
							bulkEidEasySign.isSigningInProgress ||
							isBulkAssignLoading ||
							isBulkAddCounterpartyLoading ||
							claimExecute.isPending ||
							isBulkInviteLoading ||
							isBulkRemindSignersLoading,
						onRevokeSimpleActionSelected: () =>
							setEligibleDocumentFilterMode(EligibleDocumentFilterMode.REVOKE),
						onReissueActionSelected: () =>
							setEligibleDocumentFilterMode(EligibleDocumentFilterMode.REISSUE),
						onSignActionSelected: () =>
							setEligibleDocumentFilterMode(EligibleDocumentFilterMode.SIGN),
						onAssignCounterpartySelected: () =>
							setEligibleDocumentFilterMode(
								EligibleDocumentFilterMode.ASSIGN_COUNTERPARTY
							),
						onAddCounterpartySelected: () =>
							setEligibleDocumentFilterMode(
								EligibleDocumentFilterMode.ADD_COUNTERPARTY
							),
						onClaimOnBehalfSelected: () =>
							setEligibleDocumentFilterMode(
								EligibleDocumentFilterMode.CLAIM_ON_BEHALF
							),
						onInviteAuthorisedSignatorySelected: () =>
							setEligibleDocumentFilterMode(
								EligibleDocumentFilterMode.INVITE_AUTHORISED_SIGNATORY
							),
						onRemindSignersSelected: () =>
							setEligibleDocumentFilterMode(EligibleDocumentFilterMode.REMIND),
						onApplyDateSelected: () =>
							setEligibleDocumentFilterMode(EligibleDocumentFilterMode.APPLY_DATE),
						shouldShowBulkSign: hasEidEasyDocuments,
					})
				}
				onActionCanceled={() => setEligibleDocumentFilterMode(null)}
				enableStickyFooter={true}
				mantineTableBodyRowPropsStyle={{
					minHeight: '3rem',
					height: '100%',
				}}
				rowCount={footerValues?.total ?? 0}
				isLoading={isFetching || isBulkReissueLoading}
				dataOperations={mantineTableDataOperations}
				onRowClick={handleRowClick}
			/>
			{showAssignCounterpartyModal && programmeUsers && (
				<BulkAssignCounterpartyModal
					preview={previewData?.payload ?? null}
					isLoadingPreview={isLoadingPreview}
					programmeUsers={programmeUsers}
					onClose={handleAssignCounterpartyClose}
					onConfirm={handleBulkAssignCounterpartyConfirm}
					isSubmitting={isBulkAssignLoading}
					jobStatus={assignCounterpartyJobStatus ?? null}
				/>
			)}
			{showAddCounterpartyModal && (
				<BulkAddCounterpartyModal
					preview={addCounterpartyPreview?.payload ?? null}
					isLoadingPreview={isLoadingAddCounterpartyPreview}
					isPreviewError={isAddCounterpartyPreviewError}
					assignableUsers={toAssignableUsers(programmeUsers ?? [])}
					onPreview={handleAddCounterpartyPreview}
					onConfirm={handleAddCounterpartyConfirm}
					onClose={handleAddCounterpartyClose}
					isSubmitting={isBulkAddCounterpartyLoading}
					jobId={addCounterpartyJobId}
					jobStatus={addCounterpartyJobStatus ?? null}
				/>
			)}
			{showInviteSignatoryModal && (
				<BulkInviteAuthorisedSignatoryModal
					preview={inviteSignatoryPreview?.payload ?? null}
					isLoadingPreview={isLoadingInviteSignatoryPreview}
					isSubmitting={isBulkInviteLoading}
					result={inviteSignatoryResult?.payload ?? null}
					onClose={handleInviteSignatoryClose}
					onConfirm={handleBulkInviteSignatoryConfirm}
				/>
			)}
			{showClaimOnBehalfModal && (
				<BulkClaimOnBehalfModal
					preview={claimPreview.preview}
					isLoadingPreview={claimPreview.isLoadingPreview}
					isSubmitting={claimExecute.isPending}
					result={claimExecute.data?.payload ?? null}
					generationJobStatus={claimGenerationJobStatus ?? null}
					onConfirm={handleClaimOnBehalfConfirm}
					onClose={handleClaimOnBehalfClose}
				/>
			)}
			{showRemindSignersModal && (
				<BulkRemindSignersModal
					jobStatus={remindSignersJobStatus ?? null}
					onClose={handleRemindSignersClose}
				/>
			)}
			{showRevokeSimpleModal && (
				<RevokeDocumentModal
					documents={
						documentsData?.filter((doc) => {
							if (!revokeSimpleRequestBody) {
								return false;
							}
							if (revokeSimpleRequestBody.allDocumentsSelected) {
								return true;
							}
							return revokeSimpleRequestBody.selectedDocumentWorkflowInstanceIds?.includes(
								doc.documentWorkflowInstanceId
							);
						}) ?? []
					}
					allDocumentsSelected={revokeSimpleRequestBody?.allDocumentsSelected}
					totalCount={
						(footerValues?.total ?? 0) -
						(revokeSimpleRequestBody?.excludedDocumentWorkflowInstanceIds?.length ?? 0)
					}
					onClose={() => {
						setShowRevokeSimpleModal(false);
						setRevokeSimpleRequestBody(null);
					}}
					onConfirm={handleBulkRevokeConfirm}
					isLoading={isBulkRevokeLoading}
				/>
			)}
			<EidEasySigningModal
				status={bulkEidEasySign.status}
				challengeCode={bulkEidEasySign.challengeCode}
				error={bulkEidEasySign.error}
				selectedMethod={bulkEidEasySign.selectedMethod}
				isSigningInProgress={bulkEidEasySign.isSigningInProgress}
				isModalOpen={bulkEidEasySign.isModalOpen}
				onClose={() => {
					bulkEidEasySign.reset();
					setEligibleDocumentFilterMode(null);
					if (tableRef.current) {
						getTableMeta(tableRef.current).resetActionState?.();
					}
				}}
				onSelectMethod={bulkEidEasySign.selectMethod}
				onGoBack={bulkEidEasySign.goBack}
				onConfirmSignature={bulkEidEasySign.confirmSignature}
				onSignatureConfirmed={bulkEidEasySign.confirmSignature}
				onConfirmOtp={bulkEidEasySign.confirmOtp}
				onSubmitOtpCode={bulkEidEasySign.submitOtpCode}
				onResendOtpCode={bulkEidEasySign.resendOtpCode}
				otpVerifying={bulkEidEasySign.otpVerifying}
				otpCodeError={bulkEidEasySign.otpCodeError}
				otpChannel={bulkEidEasySign.otpChannel}
				otpMaskedContact={bulkEidEasySign.otpMaskedContact}
				allowedMethods={BULK_ALLOWED_METHODS}
				progress={bulkEidEasySign.progress}
				outstandingDocuments={bulkEidEasySign.outstandingDocuments}
			/>
			{/* Mounted unconditionally, unlike the sibling modals above: conditional mounting would
			    drop its attachment to a running job every time the admin closes it. */}
			<BulkApplyDateModal
				dealId={dealId}
				programmeId={mipId}
				selection={applyDateSelection}
				opened={showApplyDateModal}
				onClose={handleApplyDateClose}
			/>
		</Flex>
	);
};
